import { useEffect, useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { gsap } from "gsap";
import { words } from "../constants";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const HeroSection = () => {
  const heroRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hero = heroRef.current;
    const title = titleRef.current;
    const subtitle = subtitleRef.current;
    const button = buttonRef.current;
    const video = videoRef.current;
    const overlay = overlayRef.current;

    if (!hero || !title || !subtitle || !button || !video || !overlay) return;

    const shouldSmooth = window.matchMedia("(min-width: 768px)").matches;
    let createdSmoother = false;
    if (!shouldSmooth) {
      ScrollSmoother.get()?.kill();
    } else if (!ScrollSmoother.get()) {
      ScrollSmoother.create({
        wrapper: "#smooth-wrapper", // must wrap whole app/page
        content: "#smooth-content",
        smooth: 2, // higher = smoother
        effects: true, // enable data-speed parallax
      });
      createdSmoother = true;
    }

    const ctx = gsap.context(() => {
      // Initial states
      gsap.set([title, subtitle, button], {
        opacity: 0,
        y: 50,
      });

      // Animation timeline
      const tl = gsap.timeline({ delay: 0.5 });

      tl.to(title, {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: "power3.out",
      })
        .to(
          subtitle,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power3.out",
          },
          "-=0.5",
        )
        .to(
          button,
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power3.out",
          },
          "-=0.3",
        );
      // Parallax effect for video + overlay
      gsap.to(video, {
        scale: 1.2,
        ease: "none",
        scrollTrigger: {
          trigger: hero,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });

      gsap.to(overlay, {
        opacity: 0.85,
        ease: "none",
        scrollTrigger: {
          trigger: hero,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }, hero);

    return () => {
      ctx.revert();
      if (createdSmoother) {
        ScrollSmoother.get()?.kill();
      }
    };
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = e.currentTarget.getAttribute("to");
    if (!target) return;

    // Only intercept in-page anchors
    if (target.startsWith("#")) {
      e.preventDefault();
      const element = document.querySelector(target);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <>
      <section
        ref={heroRef}
        id="hero"
        className=" relative overflow-hidden bg-[#f5f5f5]"
      >
        {/* Background Video */}
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="absolute top-0 left-0 w-full h-full object-cover z-0"
          data-speed="0.8"
        >
          <source src="/videos/bg_video.mp4" type="video/mp4" />
        </video>

        {/* Dark Overlay */}
        <div
          ref={overlayRef}
          className="absolute top-0 left-0 w-full h-full bg-[#0d1821]/70 z-5"
          data-speed="0.9"
        ></div>

        <div className="container mx-auto px-1 md:px-5 py-20 min-h-screen flex items-center justify-center z-10">
          <div className="grid lg:grid-cols-[1fr_200px] gap-12 items-center">
            {/* Left Content */}
            <div className="text-left px-3 md:px-0">
              <h1
                ref={titleRef}
                className="px-4 sm:px-0 text-3xl sm:text-4xl md:text-6xl lg:text-6xl text-[#f5f5f5] mb-6 font-orbitron leading-tight"
                data-speed="1.2"
              >
                <div>Your one-stop</div>
                <div className="pb-1 md:pb-0">solution for</div>
                <div className="slide h-[75px] sm:h-[48px] md:h-[130px] lg:h-[70px] md:translate-y-1 translate-y-0 overflow-hidden">
                  <span className="wrapper block">
                    {words.map((word, index) => (
                      <span
                        key={index}
                        className="flex items-center md:gap-3 gap-1 md:pb-2 font-[500]"
                      >
                        <span className="text-[#c3f620] ">{word.text}</span>
                      </span>
                    ))}
                  </span>
                </div>
              </h1>

              <p
                ref={subtitleRef}
                className="text-justify md:text-left px-4 md:px-0 md:pr-20 text-md md:text-xl text-[#f5f5f5]/80 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-poppins "
                data-speed="1.1"
              >
                We design powerful content and branding that increase
                engagement, strengthen your presence, and indirectly grow your
                revenue over time.
              </p>

              <div
                ref={buttonRef}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start px-4 md:px-0"
                data-speed="1.05"
              >
                <Link
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-12 rounded-full px-8 py-3 text-lg font-medium group font-orbitron text-[#0d1821] bg-[#f5f5f5] hover:bg-[#f5f5f5]/90"
                  to={"?form=estimate"}
                >
                  Start Your Journey
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:rotate-45 transition-all duration-300 ease-in-out [transform-origin:40%_50%] group-hover:scale-125 group-hover:-translate-y-[2px]" />
                </Link>

                <Link
                  to="/portfolio"
                  className="text-[#0d1821] px-8 py-3 text-lg font-medium active:bg-[#00a878] active:text-[#f5f5f5] lg:hover:bg-[#00a878] inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full lg:hover:text-[#f5f5f5] transition-all duration-500 h-12 bg-[#c3f620]"
                >
                  <Sparkles className="mr-2 w-5 h-5" />
                  Our Portfolio
                </Link>
              </div>
            </div>

            {/* Right Content - 3D Model Placeholder */}
            <div className="flex justify-center lg:justify-end"></div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-2 h-2 bg-primary rounded-full animate-twinkle opacity-60" />
        <div
          className="absolute top-40 right-20 w-1 h-1 bg-primary rounded-full animate-twinkle opacity-80"
          style={{ animationDelay: "1s" }}
        />
        <div
          className="absolute bottom-32 left-1/4 w-1.5 h-1.5 bg-primary rounded-full animate-twinkle opacity-70"
          style={{ animationDelay: "2s" }}
        />
      </section>
    </>
  );
};

export default HeroSection;
